import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { endpoints, formatApiError } from "../../lib/api";
import {
    Scroll, Plus, CircleNotch, Trash, UploadSimple, DownloadSimple,
    MagnifyingGlass, Buildings, Shield, X,
} from "@phosphor-icons/react";

const TYPE_LABEL = { msa: "MSA", nda: "NDA", other: "Other" };
const STATUS_META = {
    draft: { cls: "chip-gray", label: "Draft" },
    sent: { cls: "chip-orange", label: "Sent" },
    signed: { cls: "chip-green", label: "Signed" },
    expired: { cls: "chip-red", label: "Expired" },
};
const STATUS_FILTERS = [
    { key: "all", label: "All" },
    { key: "draft", label: "Draft" },
    { key: "sent", label: "Sent" },
    { key: "signed", label: "Signed" },
    { key: "expired", label: "Expired" },
];

export default function Contracts() {
    const navigate = useNavigate();
    const [contracts, setContracts] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ client_id: "", title: "", type: "msa", status: "draft", effective_date: "", expiry_date: "", notes: "" });
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [uploadingId, setUploadingId] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const [c, cl] = await Promise.all([endpoints.contracts(), endpoints.clients()]);
            setContracts(c.data || []);
            setClients(cl.data || []);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    const cmap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);

    const rows = useMemo(() => {
        let r = contracts;
        if (statusFilter !== "all") r = r.filter((c) => c.status === statusFilter);
        if (q) {
            const hay = q.toLowerCase();
            r = r.filter((c) => `${c.title} ${cmap[c.client_id]?.name || ""}`.toLowerCase().includes(hay));
        }
        return r;
    }, [contracts, statusFilter, q, cmap]);

    const counts = useMemo(() => {
        const c = { all: contracts.length, draft: 0, sent: 0, signed: 0, expired: 0 };
        contracts.forEach((x) => { c[x.status] = (c[x.status] || 0) + 1; });
        return c;
    }, [contracts]);

    async function add() {
        if (!form.client_id || !form.title.trim()) return;
        setBusy(true);
        try {
            const { data: contract } = await endpoints.createContract(form);
            if (file) {
                const fd = new FormData();
                fd.append("file", file);
                fd.append("kind", "contract");
                const { data: uploaded } = await endpoints.uploadFile(fd);
                await endpoints.updateContract(contract.id, { file_id: uploaded.id, file_name: uploaded.original_filename || file.name, file_size: uploaded.size });
            }
            setForm({ client_id: "", title: "", type: "msa", status: "draft", effective_date: "", expiry_date: "", notes: "" });
            setFile(null);
            setAdding(false);
            load();
        } catch (e) { alert(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function setStatus(id, status) {
        try { await endpoints.updateContract(id, { status }); load(); }
        catch (e) { alert(formatApiError(e)); }
    }

    async function attachFile(id, f) {
        setUploadingId(id);
        try {
            const fd = new FormData();
            fd.append("file", f);
            fd.append("kind", "contract");
            const { data: uploaded } = await endpoints.uploadFile(fd);
            await endpoints.updateContract(id, { file_id: uploaded.id, file_name: uploaded.original_filename || f.name, file_size: uploaded.size });
            load();
        } catch (e) { alert(formatApiError(e)); }
        finally { setUploadingId(null); }
    }

    async function remove(id) {
        if (!window.confirm("Delete this contract record?")) return;
        try { await endpoints.deleteContract(id); load(); }
        catch (e) { alert(formatApiError(e)); }
    }

    return (
        <div className="max-w-[1400px] mx-auto px-6 py-8">
            <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">CRM</div>
                    <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-3">
                        <Scroll size={28} weight="bold" className="text-[var(--rom-orange)]" /> Contracts
                    </h1>
                    <p className="text-sm text-[var(--rom-text-2)] mt-1.5">MSAs, NDAs and other legal agreements — who's on our supplier list and when it needs renewing.</p>
                </div>
                <button onClick={() => setAdding((a) => !a)} className="btn btn-primary" data-testid="new-contract-btn">
                    <Plus size={14} weight="bold" /> New contract
                </button>
            </header>

            {adding && (
                <div className="rom-card mb-5" data-testid="contract-add-form">
                    <div className="rom-card-head"><h3>New contract</h3><button onClick={() => setAdding(false)}><X size={16} /></button></div>
                    <div className="rom-card-body space-y-2">
                        <select className="input" value={form.client_id} onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value }))} data-testid="contract-client-select">
                            <option value="">Select client / insurer…</option>
                            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <input className="input" placeholder="Title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="contract-title-input" />
                        <div className="grid grid-cols-2 gap-2">
                            <select className="input" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                                <option value="msa">MSA</option><option value="nda">NDA</option><option value="other">Other</option>
                            </select>
                            <select className="input" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                                <option value="draft">Draft</option><option value="sent">Sent</option><option value="signed">Signed</option><option value="expired">Expired</option>
                            </select>
                            <div><label className="label">Effective date</label><input type="date" className="input" value={form.effective_date} onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))} /></div>
                            <div><label className="label">Expiry date</label><input type="date" className="input" value={form.expiry_date} onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))} /></div>
                        </div>
                        <textarea className="input resize-none" rows={2} placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
                        <label className="flex items-center gap-2 text-xs text-[var(--rom-text-2)] cursor-pointer">
                            <UploadSimple size={14} weight="bold" /> {file ? file.name : "Attach file (optional)"}
                            <input type="file" className="hidden" accept=".pdf,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="contract-file-input" />
                        </label>
                        <button className="btn btn-primary btn-sm" onClick={add} disabled={busy || !form.client_id || !form.title.trim()} data-testid="contract-save-btn">
                            {busy ? <CircleNotch size={13} className="animate-spin" /> : "Save contract"}
                        </button>
                    </div>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="flex items-center gap-1 bg-white border border-[var(--rom-border)] rounded-[6px] p-1">
                    {STATUS_FILTERS.map((f) => (
                        <button key={f.key} onClick={() => setStatusFilter(f.key)} data-testid={`contract-filter-${f.key}`}
                            className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-[4px] transition-colors flex items-center gap-2 ${
                                statusFilter === f.key ? "bg-[var(--rom-navy)] text-white" : "text-[var(--rom-text-2)] hover:text-[var(--rom-text)]"
                            }`}>
                            {f.label}
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${statusFilter === f.key ? "bg-white/15" : "bg-slate-100"}`}>{counts[f.key] || 0}</span>
                        </button>
                    ))}
                </div>
                <div className="relative flex-1 max-w-sm">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rom-text-3)]" />
                    <input className="input pl-9" placeholder="Search title or client…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="contract-search" />
                </div>
            </div>

            <div className="rom-card overflow-hidden">
                <table className="rom-table" data-testid="contracts-table">
                    <thead>
                        <tr>
                            <th>Title</th><th>Client</th><th>Type</th><th>Status</th><th>Effective → Expiry</th><th>File</th><th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={7} className="text-center text-[var(--rom-text-3)] py-10">Loading…</td></tr>}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={7} className="text-center text-[var(--rom-text-3)] py-10" data-testid="contracts-empty">No contracts match.</td></tr>
                        )}
                        {!loading && rows.map((ct) => {
                            const client = cmap[ct.client_id];
                            const meta = STATUS_META[ct.status] || STATUS_META.draft;
                            return (
                                <tr key={ct.id} data-testid={`contract-row-${ct.id}`}>
                                    <td className="font-semibold">{ct.title}</td>
                                    <td>
                                        {client ? (
                                            <button onClick={() => navigate(`/crm/accounts/${client.id}`)} className="text-sm hover:underline flex items-center gap-1.5">
                                                {client.type === "insurer" ? <Shield size={12} weight="bold" /> : <Buildings size={12} weight="bold" />} {client.name}
                                            </button>
                                        ) : <span className="text-[var(--rom-text-3)]">—</span>}
                                    </td>
                                    <td><span className="chip chip-gray">{TYPE_LABEL[ct.type] || "Other"}</span></td>
                                    <td>
                                        <select value={ct.status} onChange={(e) => setStatus(ct.id, e.target.value)} data-testid={`contract-status-change-${ct.id}`}
                                            className={`chip ${meta.cls}`} style={{ border: "none", cursor: "pointer" }}>
                                            <option value="draft">Draft</option><option value="sent">Sent</option><option value="signed">Signed</option><option value="expired">Expired</option>
                                        </select>
                                    </td>
                                    <td className="text-xs text-[var(--rom-text-2)]">{ct.effective_date || "—"} → {ct.expiry_date || "—"}</td>
                                    <td>
                                        {ct.file_id ? (
                                            <a href={endpoints.fileDownloadUrl(ct.file_id)} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[var(--rom-navy)] hover:underline flex items-center gap-1 w-fit" data-testid={`contract-view-file-${ct.id}`}>
                                                <DownloadSimple size={12} weight="bold" /> {ct.file_name || "View"}
                                            </a>
                                        ) : (
                                            <label className="text-xs font-semibold text-[var(--rom-text-2)] hover:text-[var(--rom-navy)] flex items-center gap-1 w-fit cursor-pointer">
                                                {uploadingId === ct.id ? <CircleNotch size={12} className="animate-spin" /> : <UploadSimple size={12} weight="bold" />} Attach
                                                <input type="file" className="hidden" accept=".pdf,.doc,.docx" onChange={(e) => e.target.files?.[0] && attachFile(ct.id, e.target.files[0])} data-testid={`contract-upload-${ct.id}`} />
                                            </label>
                                        )}
                                    </td>
                                    <td className="text-right">
                                        <button onClick={() => remove(ct.id)} className="p-1.5 rounded text-[var(--rom-text-2)] hover:bg-red-50 hover:text-[var(--rom-danger)]" data-testid={`contract-delete-${ct.id}`}>
                                            <Trash size={14} weight="bold" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
